/**
 * TanStack Query hooks for Brands
 *
 * This file provides React Query hooks for fetching and mutating brand data.
 * These hooks replace the async methods in the Zustand brands store.
 *
 * Cross-store synchronization:
 * - When a brand is deleted, related caches (products, bulk-groups, articles by brandId) are invalidated
 *
 * @module brands-queries
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { AiStatus } from '../../entity/ai';
import { type Brand, type BrandCreate } from '../../entity/api/brand';
import type { GenerateBrandContextReq } from '../../entity/api/routes/brand';
import {
  createBrand,
  generateBrandContext,
  generateBrandContextFromOnboarding,
  getBrandById,
  getBrandJobDataByJobId,
  getBrandJobStatus,
  getBrandsList,
  removeBrand,
  updateBrand,
} from '../../sdk/brand';

// ============ QUERY KEYS ============

export const brandsKeys = {
  all: ['brands'] as const,
  list: function () {
    return [...brandsKeys.all, 'list'] as const;
  },
  byId: function (id: string) {
    return [...brandsKeys.all, id] as const;
  },
  byOrgId: function (orgId: string) {
    return [...brandsKeys.all, 'org', orgId] as const;
  },
  jobStatus: function (brandId: string) {
    return [...brandsKeys.all, 'job-status', brandId] as const;
  },
};

// ============ QUERIES ============

/**
 * Fetch all brands list
 *
 * Replaces: getStores().brands.getState().list()
 */
export function useBrandsList() {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: brandsKeys.list(),
    queryFn: async function () {
      const response = await getBrandsList();
      const brands = response.data;

      // Cache each brand individually
      for (const brand of brands) {
        queryClient.setQueryData(brandsKeys.byId(brand._id), brand);
      }

      return brands;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Fetch a brand by ID
 *
 * Replaces: getStores().brands.getState().getById(id)
 */
export function useBrandById(id: string | undefined | null) {
  return useQuery({
    queryKey: brandsKeys.byId(id ?? ''),
    queryFn: async function () {
      if (id == null) {
        throw new Error('Brand ID is required');
      }
      const response = await getBrandById(id);
      return response.data;
    },
    enabled: id != null && id !== '',
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Get brands filtered by organization ID
 * Uses the brands list query and filters client-side
 *
 * Replaces: getStores().brands((s) => s.getByOrgIdFromStore(orgId))
 */
export function useBrandsByOrgId(orgId: string | undefined | null) {
  const { data: allBrands, ...rest } = useBrandsList();

  const filteredBrands = useMemo(
    function () {
      if (allBrands == null || orgId == null) {
        return [];
      }
      return allBrands.filter(function (brand) {
        return brand.organizationId === orgId;
      });
    },
    [allBrands, orgId]
  );

  return { data: filteredBrands, ...rest };
}

/**
 * Fetch brand job status (for context generation)
 *
 * Replaces: getBrandJobStatus SDK call
 */
export function useBrandJobStatus(brandId: string | undefined | null, enabled: boolean = true) {
  return useQuery({
    queryKey: brandsKeys.jobStatus(brandId ?? ''),
    queryFn: async function () {
      if (brandId == null) {
        throw new Error('Brand ID is required');
      }
      return getBrandJobStatus(brandId);
    },
    enabled: enabled && brandId != null && brandId !== '',
    staleTime: 1000 * 30, // 30 seconds - job status changes frequently
    refetchInterval: function (query) {
      // Auto-refetch every 30 seconds if job is still pending
      const { data } = query.state;
      if ((data?.status as AiStatus) === AiStatus.Pending) {
        return 30000;
      }
      return false;
    },
  });
}

// ============ MUTATIONS ============

/**
 * Create a new brand
 *
 * Replaces: getStores().brands.getState().create(data, isFromOnboarding)
 */
export function useCreateBrand() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      data,
      isFromOnboarding,
    }: {
      data: BrandCreate;
      isFromOnboarding: boolean;
    }) {
      const response = await createBrand(data, isFromOnboarding);
      return response.data;
    },
    onSuccess: function (brand) {
      // Cache the new brand
      queryClient.setQueryData(brandsKeys.byId(brand._id), brand);

      // Invalidate list queries
      void queryClient.invalidateQueries({ queryKey: brandsKeys.list() });
    },
  });
}

/**
 * Update a brand
 *
 * Replaces: getStores().brands.getState().update(id, data)
 */
export function useUpdateBrand() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({ id, data }: { id: string; data: Partial<Brand> }) {
      const response = await updateBrand(id, data);
      return response.data;
    },
    onSuccess: function (brand, { id }) {
      // Update the brand in cache
      queryClient.setQueryData(brandsKeys.byId(id), brand);

      // Update in list cache
      // eslint-disable-next-line no-use-before-define
      updateBrandInListCache(queryClient, brand);
    },
  });
}

/**
 * Remove a brand
 * Also invalidates related caches (products, bulk-groups, articles by brandId)
 *
 * Replaces: getStores().brands.getState().remove(id)
 */
export function useRemoveBrand() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (id: string) {
      await removeBrand(id);
      return id;
    },
    onSuccess: function (id) {
      // Get brand before removing to know which org list to update
      const brand = queryClient.getQueryData<Brand>(brandsKeys.byId(id));

      // Remove from cache
      queryClient.removeQueries({ queryKey: brandsKeys.byId(id) });

      // Update list cache
      if (brand != null) {
        // eslint-disable-next-line no-use-before-define
        removeBrandFromListCache(queryClient, brand);

        // Invalidate related caches - products, bulk-groups, articles by this brand
        // Using dynamic imports to avoid circular dependencies
        import('./products-queries')
          .then(function ({ productsKeys }) {
            void queryClient.invalidateQueries({
              queryKey: productsKeys.byBrandId(id),
            });
          })
          .catch(function () {
            // products-queries might not exist yet during migration
          });

        import('./bulk-groups-queries')
          .then(function ({ bulkGroupsKeys }) {
            void queryClient.invalidateQueries({
              queryKey: bulkGroupsKeys.byBrandId(id),
            });
          })
          .catch(function () {
            // bulk-groups-queries might not exist yet during migration
          });

        import('./articles-queries')
          .then(function ({ articlesKeys }) {
            void queryClient.invalidateQueries({
              queryKey: articlesKeys.byBrandId(id),
            });
          })
          .catch(function () {
            // articles-queries might not exist yet during migration
          });
      }
    },
  });
}

/**
 * Generate brand context using AI
 *
 * Replaces: generateBrandContext SDK call
 */
export function useGenerateBrandContext() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (data: GenerateBrandContextReq['body']) {
      const response = await generateBrandContext(data);
      return response;
    },
    onSuccess: function (response) {
      // Update the brand with generated context
      if (response.result != null && response.contentId != null) {
        const existingBrand = queryClient.getQueryData<Brand>(brandsKeys.byId(response.contentId));

        if (existingBrand != null) {
          const updatedBrand: Brand = {
            ...existingBrand,
            ...response.result,
          };
          queryClient.setQueryData(brandsKeys.byId(response.contentId), updatedBrand);
          // eslint-disable-next-line no-use-before-define
          updateBrandInListCache(queryClient, updatedBrand);
        }
      }

      // Invalidate job status
      if (response.contentId != null) {
        void queryClient.invalidateQueries({
          queryKey: brandsKeys.jobStatus(response.contentId),
        });
      }
    },
  });
}

/**
 * Generate brand context from onboarding flow
 *
 * Replaces: generateBrandContextFromOnboarding SDK call
 */
export function useGenerateBrandContextFromOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (data: GenerateBrandContextReq['body']) {
      const response = await generateBrandContextFromOnboarding(data);
      return response;
    },
    onSuccess: function (_response, { contentId }) {
      // Invalidate to refetch updated data
      void queryClient.invalidateQueries({
        queryKey: brandsKeys.byId(contentId),
      });
      void queryClient.invalidateQueries({
        queryKey: brandsKeys.jobStatus(contentId),
      });
    },
  });
}

/**
 * Get brand job data by job ID
 *
 * Replaces: getBrandJobDataByJobId SDK call
 */
export function useGetBrandJobData() {
  return useMutation({
    mutationFn: async function ({ jobId, contentId }: { jobId: string; contentId: string }) {
      const response = await getBrandJobDataByJobId(jobId, contentId);
      return response;
    },
  });
}

// ============ CACHE HELPERS ============

/**
 * Get brand from cache by ID
 * First checks individual brand cache, then falls back to looking in the list cache
 */
export function getBrandFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
): Brand | undefined {
  // First try to get from individual brand cache
  const individualBrand = queryClient.getQueryData<Brand>(brandsKeys.byId(id));
  if (individualBrand != null) {
    return individualBrand;
  }

  // Fall back to finding in the list cache
  const brandsList = queryClient.getQueryData<Brand[]>(brandsKeys.list());
  if (brandsList == null) {
    return undefined;
  }

  return brandsList.find(function (brand) {
    return brand?._id === id;
  });
}

/**
 * Get brands list from cache
 */
export function getBrandsListFromCache(
  queryClient: ReturnType<typeof useQueryClient>
): Brand[] | undefined {
  return queryClient.getQueryData<Brand[]>(brandsKeys.list());
}

/**
 * Get brands by organization ID from cache (client-side filter)
 * Replaces: getStores().brands.getState().getByOrgIdFromStore(orgId)
 */
export function getBrandsByOrgIdFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string
): Brand[] {
  const allBrands = getBrandsListFromCache(queryClient) ?? [];
  return allBrands.filter(function (brand) {
    return brand.organizationId === orgId;
  });
}

/**
 * Set brand in cache
 */
export function setBrandInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  brand: Brand
): void {
  queryClient.setQueryData(brandsKeys.byId(brand._id), brand);
}

/**
 * Update a brand in the list cache
 */
function updateBrandInListCache(
  queryClient: ReturnType<typeof useQueryClient>,
  brand: Brand
): void {
  queryClient.setQueryData<Brand[]>(brandsKeys.list(), function (oldList) {
    if (oldList == null) {
      return oldList;
    }
    return oldList.map(function (b) {
      if (b._id === brand._id) {
        return brand;
      }
      return b;
    });
  });
}

/**
 * Remove a brand from the list cache
 */
function removeBrandFromListCache(
  queryClient: ReturnType<typeof useQueryClient>,
  brand: Brand
): void {
  queryClient.setQueryData<Brand[]>(brandsKeys.list(), function (oldList) {
    if (oldList == null) {
      return oldList;
    }
    return oldList.filter(function (b) {
      return b._id !== brand._id;
    });
  });
}

/**
 * Invalidate all brand queries
 */
export function invalidateBrands(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: brandsKeys.all });
}

/**
 * Invalidate a specific brand query
 */
export function invalidateBrandById(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
): void {
  void queryClient.invalidateQueries({ queryKey: brandsKeys.byId(id) });
}

/**
 * Prefetch brands list
 */
export async function prefetchBrandsList(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<void> {
  await queryClient.prefetchQuery({
    queryKey: brandsKeys.list(),
    queryFn: async function () {
      const response = await getBrandsList();
      return response.data;
    },
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Fetch brands list and cache it (imperative - for use outside React components)
 * This is useful for router beforeLoad and initialization logic
 */
export async function fetchBrandsListImperative(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<Brand[]> {
  const brands = await queryClient.fetchQuery({
    queryKey: brandsKeys.list(),
    queryFn: async function () {
      const response = await getBrandsList();
      return response.data;
    },
    staleTime: 1000 * 60 * 2,
  });

  return brands;
}

/**
 * Fetch a single brand by ID and cache it (imperative - for use outside React components)
 * This is useful for router beforeLoad and initialization logic
 */
export async function fetchBrandByIdImperative(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
): Promise<Brand> {
  const brand = await queryClient.fetchQuery({
    queryKey: brandsKeys.byId(id),
    queryFn: async function () {
      const response = await getBrandById(id);
      return response.data;
    },
    staleTime: 1000 * 60 * 2,
  });

  return brand;
}
